import { NonRangeEntry, RangeEntryWrapper } from '../../Types'
import { isStringNotEmpty, isVarNotEmpty } from '../../../../../../../NonReact/utils/GlobalFunctions'

/**
 * De-duplicates and cleans range entry wrappers.
 * Remove all duplicate entries, account for the excludes and includes of the same value are allowed.
 * Removes all invalid entries.
 *  Invalid entries are:
 *    - Nulls
 *    - Empty strings
 *    - Numbers that are already included in a range.
 *    - Ranges that are not valid.
 *    - Ranges that are not in the correct order.
 *
 * @param ranges - The ranges to dedupe and clean.
 *
 * @returns - The deduped and cleaned ranges.
 */
export const dedupeAndCleanRangeEntryWrappers = (ranges: Nullable<RangeEntryWrapper>[]): RangeEntryWrapper[] =>
    ranges.reduce((accum: RangeEntryWrapper[], next: Nullable<RangeEntryWrapper>): RangeEntryWrapper[] => {
        // Check if value is null or empty.
        if (isVarNotEmpty(next)) {
            const { included, isRange, value }: RangeEntryWrapper = next

            // Check if the value is a range.
            if (isRange) {
                // Check if the range is valid.
                if (isVarNotEmpty(value.min) && isVarNotEmpty(value.max)) {
                    // Check if the range is in the correct order.
                    if (value.min < value.max) {
                        // Check if the range is not already included in the accumulated ranges.
                        if (
                            !accum.some(
                                (entry: RangeEntryWrapper): boolean =>
                                    entry.isRange &&
                                    entry.value.minDisplayValue === value.minDisplayValue &&
                                    entry.value.maxDisplayValue === value.maxDisplayValue &&
                                    included === entry.included
                            )
                        ) {
                            accum.push(next)
                        }
                    }
                }
            } else if (
                ((typeof value === 'number' && !isNaN(value)) ||
                    (typeof value === 'string' && isStringNotEmpty(value))) &&
                !(accum as NonRangeEntry[]).some(
                    (entry: NonRangeEntry): boolean =>
                        entry.displayValue === (next as NonRangeEntry).displayValue && included === entry.included
                )
            ) {
                accum.push(next)
            }
        }

        return accum
    }, [] as RangeEntryWrapper[])
